import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import { CheckCircle } from 'lucide-react';

function Register() {
  const [name, setName] = useState('');
  const [nickname, setNickname] = useState('');
  const [email, setEmail] = useState('');
  const [pixKey, setPixKey] = useState('');
  const [contact, setContact] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [fieldErrors, setFieldErrors] = useState({ name: '', password: '', email: '' });
  const [successMessage, setSuccessMessage] = useState('');
  const { register } = useAuth();
  const navigate = useNavigate();

  const validateField = (field, value) => {
    let msg = '';
    if (field === 'name' && !value.trim()) {
      msg = 'Nome é obrigatório';
    }
    if (field === 'password') {
      if (!value) msg = 'Senha é obrigatória';
      else if (value.length < 6) msg = 'Mínimo 6 caracteres';
    }
    if (field === 'email' && value.trim() && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)) {
      msg = 'Formato de email inválido';
    }
    setFieldErrors(prev => ({ ...prev, [field]: msg }));
    return msg;
  };

  const handleBlur = (field, value) => {
    validateField(field, value);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    const nameErr = validateField('name', name);
    const passErr = validateField('password', password);
    const emailErr = validateField('email', email);
    if (nameErr || passErr || emailErr) return;

    const result = await register(name, nickname, email, pixKey, contact, password);
    if (result.success) {
      setSuccessMessage('Conta criada com sucesso! Faça login.');
      setTimeout(() => navigate('/login'), 2000);
    } else {
      setError(result.error);
    }
  };

  return (
    <div className="container max-w-md">
      <h1 className="text-2xl font-bold mb-6 text-center text-primary">Registrar</h1>
      {successMessage && (
        <div className="mb-4 bg-green-100 border border-green-300 text-green-800 px-4 py-3 rounded flex items-center gap-2" role="status">
          <CheckCircle className="w-5 h-5 flex-shrink-0" aria-hidden="true" />
          <span>{successMessage}</span>
        </div>
      )}
      {error && (
        <div className="alert alert-error mb-4" role="alert">
          {error}
        </div>
      )}
      <form onSubmit={handleSubmit} className="card" noValidate>
        <div className="form-control">
          <label className="form-label" htmlFor="reg-name">Nome *</label>
          <input
            id="reg-name"
            type="text"
            className={`form-input${fieldErrors.name ? ' border-red-500' : ''}`}
            value={name}
            onChange={(e) => { setName(e.target.value); if (fieldErrors.name) validateField('name', e.target.value); }}
            onBlur={() => handleBlur('name', name)}
            placeholder="Seu nome"
            aria-invalid={fieldErrors.name ? 'true' : undefined}
            aria-describedby={fieldErrors.name ? 'reg-name-error' : undefined}
          />
          {fieldErrors.name && <p id="reg-name-error" className="text-red-600 text-sm mt-1">{fieldErrors.name}</p>}
        </div>
        <div className="form-control relative">
          <label className="form-label" htmlFor="reg-password">Senha *</label>
          <div className="relative">
            <input
              id="reg-password"
              type={showPassword ? "text" : "password"}
              className={`form-input w-full pr-10${fieldErrors.password ? ' border-red-500' : ''}`}
              value={password}
              onChange={(e) => { setPassword(e.target.value); if (fieldErrors.password) validateField('password', e.target.value); }}
              onBlur={() => handleBlur('password', password)}
              placeholder="Sua senha"
              aria-invalid={fieldErrors.password ? 'true' : undefined}
              aria-describedby={fieldErrors.password ? 'reg-password-error' : 'reg-password-help'}
            />
            <button
              type="button"
              className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-500 hover:text-primary-dark focus:ring-2 focus:ring-yellow-400 focus:ring-offset-2 focus:outline-none"
              onClick={() => setShowPassword(!showPassword)}
              aria-label={showPassword ? 'Ocultar senha' : 'Mostrar senha'}
            >
              {showPassword ? (
                <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-5 h-5">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M3.98 8.223A10.477 10.477 0 001.934 12C3.226 16.338 7.244 19.5 12 19.5c.993 0 1.953-.138 2.863-.395M6.228 6.228A10.45 10.45 0 0112 4.5c4.756 0 8.773 3.162 10.065 7.498a10.523 10.523 0 01-4.293 5.774M6.228 6.228L3 3m3.228 3.228l3.65 3.65m7.894 7.894L21 21m-3.228-3.228l-3.65-3.65m0 0a3 3 0 10-4.243-4.243m4.242 4.242L9.88 9.88" />
                </svg>
              ) : (
                <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-5 h-5">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M2.036 12.322a1.012 1.012 0 010-.639C3.423 7.51 7.36 4.5 12 4.5c4.638 0 8.573 3.007 9.963 7.178.07.207.07.431 0 .639C20.577 16.49 16.64 19.5 12 19.5c-4.638 0-8.573-3.007-9.963-7.178z" />
                  <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                </svg>
              )}
            </button>
          </div>
          {!fieldErrors.password && <p id="reg-password-help" className="text-gray-500 text-xs mt-1">Mínimo 6 caracteres</p>}
          {fieldErrors.password && <p id="reg-password-error" className="text-red-600 text-sm mt-1">{fieldErrors.password}</p>}
        </div>
        <div className="form-control">
          <label className="form-label" htmlFor="reg-nickname">Apelido (opcional)</label>
          <input
            id="reg-nickname"
            type="text"
            className="form-input"
            value={nickname}
            onChange={(e) => setNickname(e.target.value)}
            placeholder="Seu apelido"
          />
        </div>
        <div className="form-control">
          <label className="form-label" htmlFor="reg-email">Email (opcional, mas recomendado)</label>
          <input
            id="reg-email"
            type="email"
            className={`form-input${fieldErrors.email ? ' border-red-500' : ''}`}
            value={email}
            onChange={(e) => { setEmail(e.target.value); if (fieldErrors.email) validateField('email', e.target.value); }}
            onBlur={() => handleBlur('email', email)}
            placeholder="seu@email.com"
            aria-invalid={fieldErrors.email ? 'true' : undefined}
            aria-describedby={fieldErrors.email ? 'reg-email-error' : undefined}
          />
          {fieldErrors.email && <p id="reg-email-error" className="text-red-600 text-sm mt-1">{fieldErrors.email}</p>}
        </div>
        <div className="form-control">
          <label className="form-label" htmlFor="reg-pixkey">Chave PIX (opcional)</label>
          <input
            id="reg-pixkey"
            type="text"
            className="form-input"
            value={pixKey}
            onChange={(e) => setPixKey(e.target.value)}
            placeholder="Sua chave PIX para recebimento"
          />
        </div>
        <div className="form-control">
          <label className="form-label" htmlFor="reg-contact">Contato/Telefone (opcional)</label>
          <input
            id="reg-contact"
            type="text"
            className="form-input"
            value={contact}
            onChange={(e) => setContact(e.target.value)}
            placeholder="Seu telefone com DDD"
          />
        </div>
        <div className="text-sm text-gray-600 mt-2">
          * Campos obrigatórios
        </div>
        <button type="submit" className="btn btn-primary w-full mt-4 focus:ring-2 focus:ring-yellow-400 focus:ring-offset-2 focus:outline-none">
          Registrar
        </button>
      </form>
      <p className="text-center mt-4">
        Já tem uma conta? <Link to="/login" className="text-tertiary hover:underline focus:ring-2 focus:ring-yellow-400 focus:ring-offset-2 focus:outline-none rounded">Entrar</Link>
      </p>
    </div>
  );
}

export default Register;
